// Decisions & approvals (brief section 8.5). Milestone 1: decision records with recommendation,
// rationale, alternative, disagreement, confidence/unknowns, effort and next step; one founder's
// decision counts. Approval policies (both founders) and payload-bound approvals: milestone 2.
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import { useContextWorkspace } from '../components/Layout';
import { Audience, Chip, Demo, Due, Empty, ErrorBox, Field, Loading, Modal, PageHead, VisibilityPicker } from '../components/ui';
import { api, errorText } from '../lib/api';
import { day, when } from '../lib/format';
import type { Decision } from '../lib/types';
import { useApi } from '../lib/useApi';

const NEXT: Record<string, [string, string][]> = {
  proposed: [['approved', 'Approve'], ['rejected', 'Reject'], ['deferred', 'Defer'], ['needs_evidence', 'Ask for more evidence']],
  needs_evidence: [['approved', 'Approve'], ['rejected', 'Reject'], ['deferred', 'Defer'], ['proposed', 'Back to proposed']],
  deferred: [['approved', 'Approve'], ['rejected', 'Reject'], ['needs_evidence', 'Ask for more evidence'], ['proposed', 'Back to proposed']],
  approved: [['proposed', 'Reopen']],
  rejected: [['proposed', 'Reopen']],
  superseded: [],
};

export function Decisions() {
  const ws = useContextWorkspace()!;
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState('open');
  const list = useApi<Decision[]>(`/api/w/${ws.slug}/decisions${filter === 'all' ? '' : `?status=${filter}`}`);
  const openId = params.get('open');
  const [creating, setCreating] = useState(false);
  const selected = list.data?.find((d) => d.id === openId);

  return (
    <>
      <PageHead
        title="Decisions"
        sub="What needs deciding, with the recommendation and its reasons. Different AI roles are perspectives, not independent evidence."
        actions={
          <button type="button" className="btn primary" onClick={() => setCreating(true)}>
            New decision
          </button>
        }
      />
      <div className={`split${openId ? ' has-open' : ''}`}>
        <div className="split-list">
          <div className="toolbar">
            <label className="inline">
              Show{' '}
              <select value={filter} onChange={(e) => setFilter(e.target.value)}>
                <option value="open">needing a decision</option>
                <option value="approved">approved</option>
                <option value="rejected">rejected</option>
                <option value="all">all</option>
              </select>
            </label>
          </div>
          {list.error && <ErrorBox error={list.error} retry={list.reload} />}
          {!list.data && !list.error && <Loading />}
          {list.data?.length === 0 && <Empty>Nothing here.</Empty>}
          <ul className="recs selectable">
            {list.data?.map((d) => (
              <li key={d.id} className={`rec${d.id === openId ? ' current' : ''}`}>
                <button type="button" className="rec-button" onClick={() => setParams({ open: d.id })}>
                  <span className="rec-title">{d.title}</span>
                  <span className="rec-meta">
                    <Chip value={d.status} />
                    <Due iso={d.due_date} />
                    <Audience item={d} />
                    <Demo show={d.demo} />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="split-detail">
          {openId && (
            <button type="button" className="back-link phone-only" onClick={() => setParams({})}>
              <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" />
              All decisions
            </button>
          )}
          {selected ? (
            <DecisionCard
              key={selected.id}
              d={selected}
              onChanged={(nd) => {
                list.setData(list.data!.map((x) => (x.id === nd.id ? nd : x)));
              }}
            />
          ) : openId && list.data ? (
            <Empty>This decision is not in the current filter. Choose “all”.</Empty>
          ) : (
            <Empty>Select a decision to see its card.</Empty>
          )}
        </div>
      </div>
      {creating && (
        <DecisionForm
          onClose={() => setCreating(false)}
          onSaved={(d) => {
            setCreating(false);
            void list.reload();
            setParams({ open: d.id });
          }}
        />
      )}
    </>
  );
}

function DecisionCard({ d, onChanged }: { d: Decision; onChanged: (d: Decision) => void }) {
  const ws = useContextWorkspace()!;
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);

  async function move(status: string) {
    setError('');
    try {
      onChanged(await api<Decision>(`/api/w/${ws.slug}/decisions/${d.id}/status`, { body: { status, note: note || undefined } }));
      setNote('');
    } catch (e) {
      setError(errorText(e));
    }
  }

  const rows: [string, string | null][] = [
    ['Question', d.question],
    ['Recommendation', d.recommendation],
    ['Why', d.rationale],
    ['Alternatives', d.alternatives],
    ['Disagreement', d.disagreement],
    ['Unknowns', d.unknowns],
    ['Effort', d.effort_estimate],
    ['Next step', d.next_step],
  ];
  return (
    <article className="panel decision-card" aria-label={`Decision: ${d.title}`}>
      <header>
        <h2>{d.title}</h2>
        <div className="row gap wrap">
          <Chip value={d.status} />
          {d.confidence && <span className="badge">confidence: {d.confidence}</span>}
          {d.due_date && <span className="muted small">decide by {day(d.due_date)}</span>}
          <Audience item={d} />
          <Demo show={d.demo} />
        </div>
      </header>
      <dl>
        {rows.map(([k, v]) => (
          <div key={k} className="dl-row">
            <dt>{k}</dt>
            <dd>{v || <span className="muted">—</span>}</dd>
          </div>
        ))}
      </dl>
      {d.decided_by && (
        <p className="small">
          {d.status === 'approved' ? 'Approved' : 'Decided'} by {d.decided_by} on {when(d.decided_at)}
          {d.status_note ? ` — “${d.status_note}”` : ''}
        </p>
      )}
      {ws.kind === 'joint' && d.status === 'proposed' && (
        <p className="muted small">Approval policies for joint commitments (both founders must agree) arrive in milestone 2.</p>
      )}
      {(NEXT[d.status] ?? []).length > 0 && (
        <div className="decide">
          <Field label="Note (optional)">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why, or what evidence is missing" />
          </Field>
          <div className="row gap wrap">
            {NEXT[d.status].map(([s, label]) => (
              <button key={s} type="button" className={`btn ${s === 'approved' ? 'primary' : ''}`} onClick={() => void move(s)}>
                {label}
              </button>
            ))}
            {!['approved', 'rejected', 'superseded'].includes(d.status) && (
              <button type="button" className="btn ghost" onClick={() => setEditing(true)}>
                Edit
              </button>
            )}
          </div>
        </div>
      )}
      {error && (
        <p className="msg err" role="alert">
          {error}
        </p>
      )}
      {editing && (
        <DecisionForm
          decision={d}
          onClose={() => setEditing(false)}
          onSaved={(nd) => {
            setEditing(false);
            onChanged(nd);
          }}
        />
      )}
    </article>
  );
}

const TEXT_FIELDS: [keyof Decision, string, string?][] = [
  ['question', 'Question', 'What exactly needs deciding?'],
  ['recommendation', 'Recommendation'],
  ['rationale', 'Why'],
  ['alternatives', 'Alternatives'],
  ['disagreement', 'Disagreement', 'Where the founders or advisers disagree'],
  ['unknowns', 'Unknowns'],
  ['next_step', 'Next step'],
];

function DecisionForm({ decision, onClose, onSaved }: { decision?: Decision; onClose: () => void; onSaved: (d: Decision) => void }) {
  const ws = useContextWorkspace()!;
  const [form, setForm] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = { title: decision?.title ?? '', confidence: decision?.confidence ?? '', effort_estimate: decision?.effort_estimate ?? '', due_date: decision?.due_date ?? '' };
    TEXT_FIELDS.forEach(([k]) => (init[k] = (decision?.[k] as string | null) ?? ''));
    return init;
  });
  const [vis, setVis] = useState<'workspace' | 'personal'>(decision?.visibility ?? 'workspace');
  const [error, setError] = useState('');

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const body: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(form)) body[k] = v === '' ? (decision ? '' : undefined) : v;
    if (!form.confidence) body.confidence = undefined;
    if (!form.due_date) body.due_date = undefined;
    if (!decision || (decision.private && vis === 'workspace')) body.visibility = vis;
    try {
      onSaved(
        await api<Decision>(decision ? `/api/w/${ws.slug}/decisions/${decision.id}` : `/api/w/${ws.slug}/decisions`, {
          method: decision ? 'PATCH' : 'POST',
          body,
        }),
      );
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <Modal title={decision ? 'Edit decision' : 'New decision'} onClose={onClose} wide>
      <form onSubmit={save}>
        <Field label="Title">
          <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={300} />
        </Field>
        {TEXT_FIELDS.map(([k, label, hint]) => (
          <Field key={k} label={label} hint={hint}>
            <textarea rows={2} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
          </Field>
        ))}
        <div className="row gap wrap">
          <Field label="Confidence">
            <select value={form.confidence} onChange={(e) => setForm({ ...form, confidence: e.target.value })}>
              <option value="">not stated</option>
              <option value="low">low</option>
              <option value="medium">medium</option>
              <option value="high">high</option>
            </select>
          </Field>
          <Field label="Effort">
            <input value={form.effort_estimate} onChange={(e) => setForm({ ...form, effort_estimate: e.target.value })} placeholder="e.g. about 80 hours" />
          </Field>
          <Field label="Decide by">
            <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </Field>
        </div>
        {(!decision || decision.private) && <VisibilityPicker value={vis} onChange={setVis} workspaceName={ws.name} />}
        {error && <p className="msg err">{error}</p>}
        <footer className="modal-foot">
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn primary">
            Save
          </button>
        </footer>
      </form>
    </Modal>
  );
}
